import { type Dayjs } from 'dayjs';
import { type ReactElement, useCallback, useMemo, useState } from 'react';
import { PipelineRunsTable } from '@/features/pipelines/components/PipelineRunsTable';
import { PipelinesTable } from '@/features/pipelines/components/PipelinesTable';
import { queryPipelineRunSummary, queryPipelines } from '@/features/pipelines/data';
import {
  pipelineDefaultColumnVisibilityModel,
  pipelineDefaultSortDirection,
  pipelineDefaultSortKey,
  type PipelineSortDirection,
  pipelineSortFieldMap,
  type PipelineSortKey,
  type PipelineTableStatus,
} from '@/features/pipelines/pages/pipelinesTableDefinition';
import {
  type DateQuickFilter,
  PipelinesTableToolbar,
} from '@/features/pipelines/pages/PipelinesTableToolbar';
import {
  pipelineStatusDisplayOptions,
  type PipelineStatusKey,
} from '@/features/quality/components/qualityStatusDataHelpers';
import { formatRangeWindowNoDataText } from '@/features/quality/hooks';
import { TABLE_PANEL_HEADER_HEIGHT } from '@/features/shared/components/tableSystem/tableConstants';
import { buildTableDividerPseudoSx } from '@/features/shared/components/tableSystem/tableDividerSx';
import { tablePanelSurfaceSx } from '@/features/shared/components/tableSystem/tablePanelSurfaceSx';
import {
  tablePanelTabSx,
  tablePanelTabsSx,
} from '@/features/shared/components/tableSystem/tablePanelTabs';
import { TableToolbar } from '@/features/shared/components/tableSystem/TableToolbar';
import { useDateWindow } from '@/features/shared/hooks/useDateWindow';
import { getDetailLabel } from '@/shared/copy/details';
import { getEmptyStateMessage } from '@/shared/copy/emptyStates';
import { gridPaths, queryKeys } from '@/shared/hooks';
import { Stack } from '@/ui';
import {
  buildContainsFilter,
  buildInFilter,
  buildInFilterWithNull,
  mergeFilters,
} from '@/ui/composites/grid/gridFilters';
import { Panel } from '@/ui/composites/Panel';
import { Box, type GridColumnVisibilityModel, Tab, Tabs } from '@/ui/primitives';
import {
  applyFilteredRunSummariesToPipelines,
  fetchFilteredPipelineRunSummaries,
  groupPipelineRunSummariesByPipelineId,
} from '../hooks/pipelineFilteredRows';
import {
  buildBuildPipelineFilters,
  buildPipelineEntityFilters,
} from '../hooks/pipelinePageFilters';
import { toPipelineRunDisplayStatusFilters } from '../hooks/pipelineStatusScope';
import type { TableQueryMeta } from '@/features/shared/components/tableSystem/tableToolbar.helpers';
import type { TableDropdownOption } from '@/features/shared/components/tableSystem/tableToolbar.types';
import type { TagOption } from '@/shared/data/tags';
import type { ApiSort, GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type {
  BuildStatus,
  PipelineListItemDto,
  PipelineRunSummaryItemDto,
  QueryResult,
} from '@flowqa/shared';

type PipelinesPanelTab = 'pipelines' | 'jobs';
type BuildSortDirection = 'asc' | 'desc';
type BuildSortKey = 'startedAt' | 'finishedAt' | 'status' | 'pipelineName' | 'buildKey';

const buildSortFieldMap: Record<BuildSortKey, string> = {
  startedAt: 'startedAt',
  finishedAt: 'finishedAt',
  status: 'status',
  pipelineName: 'pipelineName',
  buildKey: 'buildKey',
};

const buildSortOptions: Array<{ key: BuildSortKey; label: string }> = [
  { key: 'startedAt', label: getDetailLabel('started') },
  { key: 'finishedAt', label: getDetailLabel('finished') },
  { key: 'status', label: getDetailLabel('status') },
  { key: 'pipelineName', label: getDetailLabel('pipeline') },
  { key: 'buildKey', label: getDetailLabel('run') },
];

const buildColumnOptions: Array<{ field: string; label: string; mandatory?: boolean }> = [
  { field: 'pipelineName', label: getDetailLabel('pipeline') },
  { field: 'buildKey', label: getDetailLabel('run'), mandatory: true },
  { field: 'status', label: getDetailLabel('status') },
  { field: 'actions', label: getDetailLabel('actions') },
];

const buildDefaultColumnVisibilityModel: GridColumnVisibilityModel = {
  pipelineName: true,
  buildKey: true,
  status: true,
  actions: true,
};

const buildDefaultSortKey: BuildSortKey = 'startedAt';
const buildDefaultSortDirection: BuildSortDirection = 'desc';

export type PipelinesTablePanelProps = {
  activeTab: PipelinesPanelTab;
  onActiveTabChange: (nextTab: PipelinesPanelTab) => void;
  selectedProjectId: string | null;
  selectedOrganisationId: string | null;
  projectFilterProjects?: string[];
  onProjectFilterProjectsChange?: (next: string[]) => void;
  projectFilterOptions?: Array<{ value: string; label: string }>;
  deltasEnabled: boolean;
  pipelineSearchValue: string;
  onPipelineSearchChange: (value: string) => void;
  pipelineFilterStatuses: PipelineTableStatus[];
  onPipelineFilterStatusesChange: (next: PipelineTableStatus[]) => void;
  pipelineFilterPipelines: string[];
  onPipelineFilterPipelinesChange: (next: string[]) => void;
  pipelineOptions: TableDropdownOption[];
  pipelineFilterTags: string[];
  onPipelineFilterTagsChange: (next: string[]) => void;
  pipelineDateQuickFilter: DateQuickFilter;
  onPipelineDateQuickFilterChange: (next: DateQuickFilter) => void;
  pipelineFromDate: Dayjs | null;
  pipelineToDate: Dayjs | null;
  latestQueryStartDay: string | null;
  latestQueryEndDay: string | null;
  onPipelineFromDateChange: (next: Dayjs | null) => void;
  onPipelineToDateChange: (next: Dayjs | null) => void;
  tagOptions: TagOption[];
  windowedStatusPipelineIds: string[] | null;
};

const toLatestBuildStatusFilterValues = (
  statuses: PipelineStatusKey[],
): Array<BuildStatus | null> => {
  const mapped = statuses.flatMap((status): Array<BuildStatus | null> => {
    if (status === 'none') return [null];
    if (status === 'success') return ['completed'];
    if (status === 'running') return ['running'];
    if (status === 'queued') return ['queued'];
    return ['failed'];
  });
  return Array.from(new Set(mapped));
};

const resolveEffectiveBuildFilterStatuses = (
  pageStatuses: PipelineStatusKey[],
  runTableStatuses: PipelineStatusKey[],
): PipelineStatusKey[] => {
  if (pageStatuses.length === 0) {
    return runTableStatuses;
  }

  if (runTableStatuses.length === 0) {
    return pageStatuses;
  }

  const pageStatusSet = new Set(pageStatuses);
  return runTableStatuses.filter((status) => pageStatusSet.has(status));
};

export const PipelinesTablePanel = ({
  activeTab,
  onActiveTabChange,
  selectedProjectId,
  selectedOrganisationId,
  projectFilterProjects,
  onProjectFilterProjectsChange,
  projectFilterOptions,
  deltasEnabled,
  pipelineSearchValue,
  onPipelineSearchChange,
  pipelineFilterStatuses,
  onPipelineFilterStatusesChange,
  pipelineFilterPipelines,
  onPipelineFilterPipelinesChange,
  pipelineOptions,
  pipelineFilterTags,
  onPipelineFilterTagsChange,
  pipelineDateQuickFilter,
  onPipelineDateQuickFilterChange,
  pipelineFromDate,
  pipelineToDate,
  latestQueryStartDay,
  latestQueryEndDay,
  onPipelineFromDateChange,
  onPipelineToDateChange,
  tagOptions,
  windowedStatusPipelineIds,
}: PipelinesTablePanelProps): ReactElement => {
  const [pipelineColumnVisibilityModel, setPipelineColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>(pipelineDefaultColumnVisibilityModel);
  const [pipelineSortKey, setPipelineSortKey] = useState<PipelineSortKey>(pipelineDefaultSortKey);
  const [pipelineSortDirection, setPipelineSortDirection] = useState<PipelineSortDirection>(
    pipelineDefaultSortDirection,
  );
  const [pipelineMeta, setPipelineMeta] = useState<TableQueryMeta<PipelineListItemDto> | null>(
    null,
  );
  const [debouncedBuildSearch, setDebouncedBuildSearch] = useState('');
  const [buildDateQuickFilter, setBuildDateQuickFilter] = useState<DateQuickFilter>('7d');
  const [buildFromDate, setBuildFromDate] = useState<Dayjs | null>(null);
  const [buildToDate, setBuildToDate] = useState<Dayjs | null>(null);
  const [buildColumnVisibilityModel, setBuildColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>(buildDefaultColumnVisibilityModel);
  const [buildSortKey, setBuildSortKey] = useState<BuildSortKey>(buildDefaultSortKey);
  const [buildSortDirection, setBuildSortDirection] =
    useState<BuildSortDirection>(buildDefaultSortDirection);
  const [buildFilterStatuses, setBuildFilterStatuses] = useState<PipelineStatusKey[]>([]);
  const [buildFilterTags, setBuildFilterTags] = useState<string[]>([]);
  const [buildMeta, setBuildMeta] = useState<TableQueryMeta<PipelineRunSummaryItemDto> | null>(
    null,
  );

  const availableTags = useMemo(
    () =>
      Array.from(new Set(tagOptions.map((option) => option.value))).sort((left, right) =>
        left.localeCompare(right),
      ),
    [tagOptions],
  );
  const pipelineDateWindow = useDateWindow({
    dateQuickFilter: pipelineDateQuickFilter,
    fromDate: pipelineFromDate,
    toDate: pipelineToDate,
    latestQueryStartDay,
    latestQueryEndDay,
  });
  const buildDateWindow = useDateWindow({
    dateQuickFilter: buildDateQuickFilter,
    fromDate: buildFromDate,
    toDate: buildToDate,
    latestQueryStartDay,
    latestQueryEndDay,
  });

  const handleBuildDateQuickFilterChange = useCallback((next: DateQuickFilter) => {
    setBuildDateQuickFilter(next);
    setBuildFromDate(null);
    setBuildToDate(null);
  }, []);

  const pipelineBuildStatusFilters = useMemo<GridQueryInput['filters']>(() => {
    if (pipelineFilterStatuses.length === 0) return [];
    const values = toLatestBuildStatusFilterValues(pipelineFilterStatuses);
    return buildInFilterWithNull('latestRunStatus', values);
  }, [pipelineFilterStatuses]);
  const effectiveBuildFilterStatuses = useMemo(
    () => resolveEffectiveBuildFilterStatuses(pipelineFilterStatuses, buildFilterStatuses),
    [buildFilterStatuses, pipelineFilterStatuses],
  );
  const buildDisplayStatusFilters = useMemo(
    () => toPipelineRunDisplayStatusFilters(effectiveBuildFilterStatuses),
    [effectiveBuildFilterStatuses],
  );

  const pipelineQueryFn = useCallback(
    async (input: GridQueryInput): Promise<QueryResult<PipelineListItemDto>> => {
      if (windowedStatusPipelineIds !== null && windowedStatusPipelineIds.length === 0) {
        return {
          items: [],
          total: 0,
          page: input.page,
          pageSize: input.pageSize,
        };
      }

      const toolbarFilters = mergeFilters(
        buildContainsFilter('name', pipelineSearchValue),
        windowedStatusPipelineIds === null
          ? pipelineBuildStatusFilters
          : buildPipelineEntityFilters(windowedStatusPipelineIds),
        buildPipelineEntityFilters(pipelineFilterPipelines),
        buildInFilter('tags', pipelineFilterTags),
      );
      const sortOverride: ApiSort = {
        field: pipelineSortFieldMap[pipelineSortKey],
        direction: pipelineSortDirection,
      };
      const result = await queryPipelines(selectedProjectId, selectedOrganisationId ?? undefined, {
        ...input,
        sorts: [sortOverride],
        filters: [...input.filters, ...toolbarFilters],
        metricsStartDay: pipelineDateWindow.startDay ?? undefined,
        metricsEndDay: pipelineDateWindow.endDay ?? undefined,
        previousMetricsStartDay: pipelineDateWindow.previousStartDay ?? undefined,
        previousMetricsEndDay: pipelineDateWindow.previousEndDay ?? undefined,
        include: {
          latestRun: true,
          runSummaries: true,
          metrics: true,
          metricDeltas: deltasEnabled,
        },
      });

      if (windowedStatusPipelineIds === null || pipelineFilterStatuses.length === 0) {
        return result;
      }

      const filteredRunSummaries = await fetchFilteredPipelineRunSummaries({
        selectedProjectId,
        selectedOrganisationId,
        pipelineIds: result.items.map((pipeline) => pipeline.id),
        statuses: pipelineFilterStatuses,
        selectedTags: pipelineFilterTags,
        startDay: pipelineDateWindow.startDay,
        endDay: pipelineDateWindow.endDay,
      });

      return {
        ...result,
        items: applyFilteredRunSummariesToPipelines(
          result.items,
          groupPipelineRunSummariesByPipelineId(filteredRunSummaries),
        ),
      };
    },
    [
      deltasEnabled,
      pipelineSearchValue,
      pipelineDateWindow.endDay,
      pipelineDateWindow.previousEndDay,
      pipelineDateWindow.previousStartDay,
      pipelineDateWindow.startDay,
      pipelineBuildStatusFilters,
      pipelineFilterPipelines,
      pipelineFilterStatuses,
      pipelineFilterTags,
      pipelineSortDirection,
      pipelineSortKey,
      selectedOrganisationId,
      selectedProjectId,
      windowedStatusPipelineIds,
    ],
  );

  const buildQueryFn = useCallback(
    async (input: GridQueryInput): Promise<QueryResult<PipelineRunSummaryItemDto>> => {
      const toolbarFilters = mergeFilters(
        buildContainsFilter('pipelineName', debouncedBuildSearch),
        buildBuildPipelineFilters(pipelineFilterPipelines),
        buildInFilter('displayStatus', buildDisplayStatusFilters),
        buildInFilter('tags', buildFilterTags),
      );
      const sortOverride: ApiSort = {
        field: buildSortFieldMap[buildSortKey],
        direction: buildSortDirection,
      };
      const result = await queryPipelineRunSummary(
        selectedProjectId,
        selectedOrganisationId ?? undefined,
        {
          page: input.page,
          pageSize: input.pageSize,
          sorts: [sortOverride],
          filters: [...input.filters, ...toolbarFilters],
          metricsStartDay: buildDateWindow.startDay ?? undefined,
          metricsEndDay: buildDateWindow.endDay ?? undefined,
        },
      );
      return {
        items: result.runSummaries,
        total: result.buildTotal,
        page: input.page,
        pageSize: input.pageSize,
      };
    },
    [
      buildDisplayStatusFilters,
      pipelineFilterPipelines,
      buildFilterTags,
      buildSortDirection,
      buildSortKey,
      buildDateWindow.endDay,
      buildDateWindow.startDay,
      debouncedBuildSearch,
      selectedOrganisationId,
      selectedProjectId,
    ],
  );

  const pipelineNoRowsText = pipelineDateWindow.hasCustomBounds
    ? getEmptyStateMessage('entity.noneForSelectedRange', { entity: 'pipeline' })
    : formatRangeWindowNoDataText(
        getEmptyStateMessage('entity.noneFound', { entity: 'pipeline' }),
        pipelineDateQuickFilter,
        false,
      );
  const buildNoRowsText = buildDateWindow.hasCustomBounds
    ? getEmptyStateMessage('entity.noneForSelectedRange', { entity: 'pipelineRun' })
    : formatRangeWindowNoDataText(
        getEmptyStateMessage('entity.noneFound', { entity: 'pipelineRun' }),
        buildDateQuickFilter,
        false,
      );
  const pipelineStatusFilterKey = useMemo(
    () => [...pipelineFilterStatuses].sort((left, right) => left.localeCompare(right)).join(','),
    [pipelineFilterStatuses],
  );
  const pipelineTagFilterKey = useMemo(
    () => [...pipelineFilterTags].sort((left, right) => left.localeCompare(right)).join(','),
    [pipelineFilterTags],
  );
  const buildStatusFilterKey = useMemo(
    () =>
      [...effectiveBuildFilterStatuses].sort((left, right) => left.localeCompare(right)).join(','),
    [effectiveBuildFilterStatuses],
  );
  const buildTagFilterKey = useMemo(
    () => [...buildFilterTags].sort((left, right) => left.localeCompare(right)).join(','),
    [buildFilterTags],
  );
  const windowedStatusPipelineIdsKey = useMemo(
    () =>
      windowedStatusPipelineIds === null
        ? 'latest-snapshot'
        : [...windowedStatusPipelineIds].sort((left, right) => left.localeCompare(right)).join(','),
    [windowedStatusPipelineIds],
  );

  return (
    <Panel
      padding="md"
      gap="none"
      contentGap="md"
      headerGap="none"
      headerSlot={
        <Box
          sx={{
            borderBottom: 'none',
            minHeight: `${TABLE_PANEL_HEADER_HEIGHT}px`,
            position: 'relative',
            '&::after': buildTableDividerPseudoSx('bottom'),
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap="md"
            sx={{
              minWidth: 0,
              minHeight: `${TABLE_PANEL_HEADER_HEIGHT}px`,
              height: `${TABLE_PANEL_HEADER_HEIGHT}px`,
            }}
          >
            <Box sx={{ minWidth: 0, flex: '1 1 auto' }}>
              <Tabs
                value={activeTab}
                onChange={(_event, next) => {
                  onActiveTabChange(next as PipelinesPanelTab);
                }}
                aria-label="Pipelines table tabs"
                sx={tablePanelTabsSx}
              >
                <Tab label="Pipelines" value="pipelines" sx={tablePanelTabSx} />
                <Tab label="Runs" value="jobs" sx={tablePanelTabSx} />
              </Tabs>
            </Box>
            {activeTab === 'pipelines' ? (
              <Box sx={{ flex: '0 0 auto' }}>
                <PipelinesTableToolbar
                  searchValue={pipelineSearchValue}
                  onDebouncedSearchChange={onPipelineSearchChange}
                  columnVisibilityModel={pipelineColumnVisibilityModel}
                  onColumnVisibilityModelChange={setPipelineColumnVisibilityModel}
                  sortKey={pipelineSortKey}
                  sortDirection={pipelineSortDirection}
                  onSortChange={(nextSortKey, nextSortDirection) => {
                    setPipelineSortKey(nextSortKey);
                    setPipelineSortDirection(nextSortDirection);
                  }}
                  filterStatuses={pipelineFilterStatuses}
                  onFilterStatusesChange={onPipelineFilterStatusesChange}
                  filterEntities={pipelineFilterPipelines}
                  onFilterEntitiesChange={onPipelineFilterPipelinesChange}
                  entityOptions={pipelineOptions}
                  filterTags={pipelineFilterTags}
                  onFilterTagsChange={onPipelineFilterTagsChange}
                  filterProjects={projectFilterProjects}
                  onFilterProjectsChange={onProjectFilterProjectsChange}
                  projectOptions={projectFilterOptions}
                  dateQuickFilter={pipelineDateQuickFilter}
                  onDateQuickFilterChange={onPipelineDateQuickFilterChange}
                  fromDate={pipelineFromDate}
                  toDate={pipelineToDate}
                  latestQueryStartDay={latestQueryStartDay}
                  latestQueryEndDay={latestQueryEndDay}
                  onFromDateChange={onPipelineFromDateChange}
                  onToDateChange={onPipelineToDateChange}
                  availableTags={availableTags}
                  summaryCount={pipelineMeta?.total ?? null}
                />
              </Box>
            ) : (
              <Box sx={{ flex: '0 0 auto' }}>
                <TableToolbar<BuildSortKey, PipelineStatusKey>
                  title={undefined}
                  searchAriaLabel="Search pipeline runs by pipeline"
                  searchValue={debouncedBuildSearch}
                  onDebouncedSearchChange={setDebouncedBuildSearch}
                  columnVisibilityModel={buildColumnVisibilityModel}
                  defaultColumnVisibilityModel={buildDefaultColumnVisibilityModel}
                  columnOptions={buildColumnOptions}
                  onColumnVisibilityModelChange={setBuildColumnVisibilityModel}
                  sortKey={buildSortKey}
                  sortDirection={buildSortDirection}
                  defaultSortKey={buildDefaultSortKey}
                  defaultSortDirection={buildDefaultSortDirection}
                  sortOptions={buildSortOptions}
                  onSortChange={(nextSortKey, nextSortDirection) => {
                    setBuildSortKey(nextSortKey);
                    setBuildSortDirection(nextSortDirection);
                  }}
                  filterStatuses={buildFilterStatuses}
                  statusOptions={pipelineStatusDisplayOptions.filter(
                    (option) => option.value !== 'none',
                  )}
                  onFilterStatusesChange={setBuildFilterStatuses}
                  filterEntities={pipelineFilterPipelines}
                  onFilterEntitiesChange={onPipelineFilterPipelinesChange}
                  entityOptions={pipelineOptions}
                  entityLabel="Pipeline"
                  filterTags={buildFilterTags}
                  onFilterTagsChange={setBuildFilterTags}
                  dateQuickFilter={buildDateQuickFilter}
                  onDateQuickFilterChange={handleBuildDateQuickFilterChange}
                  fromDate={buildFromDate}
                  toDate={buildToDate}
                  latestQueryStartDay={latestQueryStartDay}
                  latestQueryEndDay={latestQueryEndDay}
                  onFromDateChange={setBuildFromDate}
                  onToDateChange={setBuildToDate}
                  availableTags={availableTags}
                  summaryCount={buildMeta?.total ?? null}
                  summarySingularLabel="pipeline run"
                  summaryPluralLabel="pipeline runs"
                  hideDateRangeLabel
                  showControlsWithoutSummary
                />
              </Box>
            )}
          </Stack>
        </Box>
      }
      sx={tablePanelSurfaceSx}
    >
      {activeTab === 'pipelines' ? (
        <PipelinesTable
          persistKey={null}
          queryKey={[
            ...queryKeys.dataGrid(gridPaths.pipelines),
            pipelineDateQuickFilter,
            selectedOrganisationId ?? null,
            selectedProjectId ?? null,
            deltasEnabled ? 'deltas-on' : 'deltas-off',
            pipelineSearchValue,
            pipelineSortKey,
            pipelineSortDirection,
            pipelineStatusFilterKey,
            pipelineTagFilterKey,
            pipelineDateWindow.startDay,
            pipelineDateWindow.endDay,
            windowedStatusPipelineIdsKey,
          ]}
          queryFn={pipelineQueryFn}
          pageSizeOptions={[5, 10, 25, 50]}
          initialPageSize={5}
          preservePreviousData
          showDeltas={deltasEnabled}
          noRowsText={pipelineNoRowsText}
          showToolbar={false}
          search={pipelineSearchValue}
          onQueryMeta={setPipelineMeta}
          tagOptionsOverride={tagOptions}
          columnVisibilityModel={pipelineColumnVisibilityModel}
          onColumnVisibilityModelChange={(nextModel) => {
            setPipelineColumnVisibilityModel({
              ...nextModel,
              name: true,
            });
          }}
        />
      ) : (
        <PipelineRunsTable
          persistKey={null}
          queryKey={[
            ...queryKeys.dataGrid(gridPaths.builds),
            buildDateQuickFilter,
            selectedOrganisationId ?? null,
            selectedProjectId ?? null,
            debouncedBuildSearch,
            buildSortKey,
            buildSortDirection,
            buildStatusFilterKey,
            buildTagFilterKey,
            buildDateWindow.startDay,
            buildDateWindow.endDay,
          ]}
          queryFn={buildQueryFn}
          pageSizeOptions={[5, 10, 25, 50]}
          initialPageSize={5}
          noRowsText={buildNoRowsText}
          showToolbar={false}
          search={debouncedBuildSearch}
          onQueryMeta={setBuildMeta}
          columnVisibilityModel={buildColumnVisibilityModel}
          onColumnVisibilityModelChange={(nextModel) => {
            setBuildColumnVisibilityModel({
              ...nextModel,
              buildKey: true,
            });
          }}
        />
      )}
    </Panel>
  );
};
